/**
 * ============================================================================
 * KbSlashMenu — Menu tipo Notion/GitBook: digita "/" no editor e abre uma
 *               lista de blocos para inserir (heading, list, quote, code,
 *               callout, steps, toggle, card grid, table, image, embed…).
 * ----------------------------------------------------------------------------
 * Implementação: Extension Tiptap baseada em @tiptap/suggestion + tippy.js.
 *
 * CHANGELOG:
 *   RELEASE: 2026-07-08 v1.0.0 - Versão inicial (Bloco 3).
 * ============================================================================
 */
import { Extension } from "@tiptap/core";
import Suggestion from "@tiptap/suggestion";
import { ReactRenderer } from "@tiptap/react";
import type { Editor, Range } from "@tiptap/core";
import tippy, { type Instance as TippyInstance } from "tippy.js";
import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { BLOCK_CATALOG, type BlockCatalogItem } from "./kbBlockCatalog";

// ---------------------------------------------------------------------------
// Definição dos itens (derivada do catálogo único de blocos)
// ---------------------------------------------------------------------------
export interface SlashItem {
  title: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  keywords: string[];
  command: (props: { editor: Editor; range: Range }) => void;
}

/**
 * Slash menu e handle "+" compartilham exatamente o mesmo catálogo
 * (`BLOCK_CATALOG`). Aqui só adaptamos a assinatura para o formato do
 * suggestion do Tiptap (range obrigatório).
 */
const ITEMS: SlashItem[] = BLOCK_CATALOG.map((item: BlockCatalogItem) => ({
  title: item.title,
  description: item.description,
  icon: item.icon,
  keywords: item.keywords,
  command: ({ editor, range }) => item.command({ editor, range }),
}));

// ---------------------------------------------------------------------------
// Painel React que aparece no popup do tippy
// ---------------------------------------------------------------------------
interface PanelProps {
  items: SlashItem[];
  command: (item: SlashItem) => void;
}
interface PanelHandle {
  onKeyDown: (props: { event: KeyboardEvent }) => boolean;
}

const SlashPanel = forwardRef<PanelHandle, PanelProps>(({ items, command }, ref) => {
  const [selected, setSelected] = useState(0);
  useEffect(() => setSelected(0), [items]);

  useImperativeHandle(ref, () => ({
    onKeyDown: ({ event }) => {
      if (event.key === "ArrowDown") {
        setSelected((i) => (i + 1) % Math.max(items.length, 1));
        return true;
      }
      if (event.key === "ArrowUp") {
        setSelected((i) => (i - 1 + Math.max(items.length, 1)) % Math.max(items.length, 1));
        return true;
      }
      if (event.key === "Enter") {
        const item = items[selected];
        if (item) command(item);
        return true;
      }
      return false;
    },
  }));

  if (items.length === 0) {
    return (
      <div className="rounded-md border bg-popover text-popover-foreground shadow-md p-2 text-xs text-muted-foreground">
        Nenhum bloco encontrado
      </div>
    );
  }

  return (
    <div className="rounded-md border bg-popover text-popover-foreground shadow-md py-1 w-72 max-h-80 overflow-auto">
      {items.map((it, idx) => {
        const Icon = it.icon;
        const active = idx === selected;
        return (
          <button
            key={it.title}
            type="button"
            onClick={() => command(it)}
            onMouseEnter={() => setSelected(idx)}
            className={`w-full text-left flex items-start gap-2 px-2 py-1.5 text-sm transition-colors ${
              active ? "bg-accent text-accent-foreground" : "hover:bg-accent/50"
            }`}
          >
            <Icon className="h-4 w-4 mt-0.5 shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="font-medium leading-tight">{it.title}</div>
              <div className="text-xs text-muted-foreground truncate">{it.description}</div>
            </div>
          </button>
        );
      })}
    </div>
  );
});
SlashPanel.displayName = "SlashPanel";

// ---------------------------------------------------------------------------
// Extension Tiptap
// ---------------------------------------------------------------------------
export const KbSlashMenu = Extension.create({
  name: "kbSlashMenu",
  addOptions() {
    return {
      suggestion: {
        char: "/",
        startOfLine: false,
        command: ({ editor, range, props }: any) => props.command({ editor, range }),
        items: ({ query }: { query: string }) => {
          const q = query.toLowerCase().trim();
          if (!q) return ITEMS;
          return ITEMS.filter(
            (it) =>
              it.title.toLowerCase().includes(q) ||
              it.keywords.some((k) => k.toLowerCase().includes(q)),
          ).slice(0, 12);
        },
        render: () => {
          let component: ReactRenderer<PanelHandle, PanelProps> | null = null;
          let popup: TippyInstance[] = [];

          return {
            onStart: (props: any) => {
              component = new ReactRenderer(SlashPanel as any, {
                props: {
                  items: props.items,
                  command: (item: SlashItem) => props.command(item),
                },
                editor: props.editor,
              });
              if (!props.clientRect) return;
              popup = tippy("body", {
                getReferenceClientRect: props.clientRect,
                appendTo: () => document.body,
                content: component.element,
                showOnCreate: true,
                interactive: true,
                trigger: "manual",
                placement: "bottom-start",
                theme: "light-border",
                offset: [0, 6],
              });
            },
            onUpdate: (props: any) => {
              component?.updateProps({
                items: props.items,
                command: (item: SlashItem) => props.command(item),
              });
              if (!props.clientRect || !popup[0]) return;
              popup[0].setProps({ getReferenceClientRect: props.clientRect });
            },
            onKeyDown: (props: any) => {
              if (props.event.key === "Escape") {
                popup[0]?.hide();
                return true;
              }
              return component?.ref?.onKeyDown(props) ?? false;
            },
            onExit: () => {
              popup[0]?.destroy();
              component?.destroy();
            },
          };
        },
      },
    };
  },
  addProseMirrorPlugins() {
    return [
      Suggestion({
        editor: this.editor,
        ...this.options.suggestion,
      }),
    ];
  },
});
